import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { API_ENDPOINTS } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // SECURITY FIX: Don't auto-login from sessionStorage
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // ALWAYS validate session with server - no auto-login from storage
    const checkAuth = async () => {
      try {
        console.log('🔐 Validating session with server...');
        const response = await api.get(API_ENDPOINTS.checkAuth);
        
        if (response.data.authenticated) {
          console.log('✅ Session validated successfully');
          console.log('📋 User data from session:', response.data.user);
          setUser(response.data.user);
          
          // Only store in sessionStorage AFTER server validation
          sessionStorage.setItem('user', JSON.stringify(response.data.user));
          
          if (response.data.user.shipEngineAPI) {
            sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
          }
          
          // Log session age for monitoring
          if (response.data.sessionAge) {
            const ageMinutes = Math.floor(response.data.sessionAge / (1000 * 60));
            console.log(`📊 Session age: ${ageMinutes} minutes`);
          }
          
          setError(null);
        } else {
          console.log('❌ Session not authenticated');
          handleUnauthenticated();
        }
      } catch (error) {
        console.error('🚨 Auth check error:', {
          message: error.message,
          status: error.response?.status
        });
        
        // Handle different error scenarios
        if (error.response?.status === 401) {
          console.log('🔒 Session expired or invalid');
          handleUnauthenticated();
        } else if (error.response?.status === 503) {
          setError('Service temporarily unavailable. Please try again in a moment.');
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          setError('Unable to connect to server. Please check your connection.');
        } else {
          setError('Authentication service error. Please try again.');
        }
        
        handleUnauthenticated();
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleUnauthenticated = () => {
    setUser(null);
    // Clear all session storage
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('shipEngineAPI');
    sessionStorage.removeItem('externalCart');
    sessionStorage.removeItem('cart');
  };

  const login = (userData) => {
    console.log('👤 User logged in:', userData.email || userData.username);
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
    
    if (userData.shipEngineAPI) {
      sessionStorage.setItem('shipEngineAPI', userData.shipEngineAPI);
    }
    
    setError(null);
  };

  const logout = async () => {
    try {
      console.log('🚪 Logging out user...');
      await api.post('/api/logout');
      console.log('✅ Server logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Continue with client-side logout even if server logout fails
    } finally {
      console.log('🧹 Clearing local session data');
      handleUnauthenticated();
      setError(null);
    }
  };

  // Automatic logout on session expiry
  const handleSessionExpiry = () => {
    console.log('⏰ Session expired - automatic logout');
    handleUnauthenticated();
    setError('Your session has expired. Please log in again.');
  };

  // Add axios interceptor to handle session expiry globally
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && user) {
          // Only handle session expiry if user was previously logged in
          console.log('🔒 401 error detected - session expired');
          handleSessionExpiry();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [user]);

  const contextValue = {
    user,
    login,
    logout,
    loading,
    error,
    setError: (error) => setError(error),
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
